import get from 'growth-payments-core/object/get';
import { CPQ_QUOTES_GATE, CPQ_UPGRADES_GATE, KYC_EMBEDDED_FALLBACK_EXPERIENCE, OPTIMIZE_POST_ENROLL_M2_M3, PRE_APPROVED, PRODUCT_BUILDER, QUOTES_SECTION_DEALS_KANBAN, SHARED_TASKS_COMPONENTS_MIGRATION } from '../constants/gates';
import { getAuth } from './user';
import { useOptinAvailability } from '../hooks/useOptinAvailability';
import { useUserInfo } from '../hooks/useUserInfo';
// @ts-expect-error - Untyped module
import { OPTIN_VIEW_NEW } from 'ui-addon-opt/constants/UIOptConstants';
import memoizeOne from 'react-utils/memoizeOne';
const EMPTY_GATES = new Set();

// gates don't change over the lifetime of the app. use a memoized helper to
// ensure that all calls to the `useGetIsUngated` hook share one Set,
// instead of each hook call/component building its own
const memoizedToGatesSet = memoizeOne(gates => new Set(gates));
export const useGates = () => {
  const userInfo = useUserInfo();
  const userInfoGates = !userInfo ? EMPTY_GATES : memoizedToGatesSet(userInfo.gates);
  return userInfoGates;
};
export const getGates = () => {
  return get(getAuth(), 'gates');
};
export const useIsUngated = (...gatesToCheck) => {
  const gates = useGates();
  return gatesToCheck.every(gate => gates.has(gate));
};
const useIsOptedIn = optInName => {
  const {
    data: optinView
  } = useOptinAvailability(optInName);
  return (optinView === null || optinView === void 0 ? void 0 : optinView.optinAvailability) === OPTIN_VIEW_NEW;
};
export const useIsUngatedForPreApproval = () => useIsUngated(PRE_APPROVED);
export const useIsUngatedForOptimizePostEnrollM2M3 = () => useIsUngated(OPTIMIZE_POST_ENROLL_M2_M3);
export const useIsUngatedForKycEmbeddedFallbackExperience = () => useIsUngated(KYC_EMBEDDED_FALLBACK_EXPERIENCE);
export const useIsUngatedForSharedTasksComponentsMigration = () => useIsUngated(SHARED_TASKS_COMPONENTS_MIGRATION);
export const useIsUngatedForQuotesSectionInDealObjectCard = () => useIsUngated(QUOTES_SECTION_DEALS_KANBAN);

/**
 * CPQ Quotes utils
 * Relevant slack post - https://hubspot.slack.com/archives/C08P01MV477/p1748643647254359?thread_ts=1748530241.285839&cid=C08P01MV477
 */
export const OPT_IN_NAME = 'cpq-quote-private-beta';
export const useIsUngatedForCpqQuotes = () => useIsUngated(CPQ_QUOTES_GATE);
export const useIsUngatedForCPQQuotesAndOptedIn = () => {
  const isUngated = useIsUngatedForCpqQuotes();
  const isOptedIn = useIsOptedIn(OPT_IN_NAME);
  return isUngated && isOptedIn;
};
export const useIsUngatedForCPQFeature = featureGate => {
  const isUngated = useIsUngated(CPQ_QUOTES_GATE, featureGate);
  const isOptedIn = useIsOptedIn(OPT_IN_NAME);
  return isUngated && isOptedIn;
};
export const useIsUngatedForProductBuilder = () => {
  const isUngatedForProductBuilder = useIsUngated(PRODUCT_BUILDER);
  const isUngatedForCpqQuotes = useIsUngatedForCpqQuotes();
  return isUngatedForProductBuilder || isUngatedForCpqQuotes;
};
export const useIsUngatedForCpqUpgrades = () => useIsUngated(CPQ_UPGRADES_GATE);

/**
 * @example
 * // Check if user is ungated and opted into CPQ quotes. If isOptedIn is not provided, it will default to true (essentially just validating gates)
 * const gates = ['commerce:cpq:bse:CPQQuote','some-feature-gate'];
 * const isOptedIn = useIsOptedIn(OPT_IN_NAME);
 * const isUngatedForCPQFeature = getIsUngatedForCPQFeature(gates, 'some-feature-gate', isOptedIn);
 */
export const getIsUngatedForCPQFeature = (gates, featureGate, isOptedIn) => {
  return gates.includes(CPQ_QUOTES_GATE) && gates.includes(featureGate) && (isOptedIn !== null && isOptedIn !== void 0 ? isOptedIn : true);
};